import {
    Input,
    Button,
    Typography,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, DocumentPlusIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import ShowLoader from "@/components/Loader";
import { getManager, fetchCountryMobileCode, fetchCountry, fetchCity, fetchOffice, fetchRole, fetchRolePermission, addInternalUser, resetStatus } from "@/store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";
import { useTranslation } from "react-i18next";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const fileValidation = (t, field, isEdit, isImageRemoved) =>
    yup
        .mixed()
        .test("fileType", t("validations.invalid_file_type"), (value) => {
            if (!value || !(value instanceof File)) return true;

            const isImage = value.type.startsWith("image/");
            const isPdf = value.type === "application/pdf";

            if (field === "profile_image") {
                // profile_image - ONLY image allowed
                return isImage;
            }
            // Default fallback - allow image + PDF
            return isImage || isPdf;
        })
        .nullable()
        .test(
            "required-check",
            // field === "id_proof" ? t("validations.id_proof_required") : t("validations.field_required", { field: field.replaceAll("_", " ").replace(/^./, (str) => str.toUpperCase()) }),
            field === "id_proof" ? t("validations.id_proof_required") : t("validations.field_required", { field: t(field) }),
            (value) => {
                if (!isEdit) return value instanceof File;
                if (isImageRemoved[field]) return value instanceof File;
                return true;
            }
        );


const schema = (t, isEdit = false, isImageRemoved = {}) =>
    yup.object({
        first_name: yup
            .string()
            .trim()
            .required(t("validations.first_name_required"))
            .matches(notBlankRegex, t("validations.first_name_invalid_format"))
            .matches(noHTMLRegex, t("validations.no_html_tags"))
            .matches(noCodeRegex, t("validations.no_code_content"))
            .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("full_name") }))
            .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("full_name") }))
            .min(2, t("validations.first_name_min"))
            .max(30, t("validations.first_name_max")),

        gender: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .required(t("validations.gender_required"))
            .oneOf(['Male', 'Female', 'Other'], t("validations.gender_invalid")),

        email: yup
            .string()
            .trim()
            .required(t("validations.email_required"))
            .email(t("validations.email_invalid"))
            .matches(notBlankRegex, t("validations.email_invalid_format"))
            .matches(noHTMLRegex, t("validations.no_html_tags"))
            .matches(noCodeRegex, t("validations.no_code_content"))
            .max(50, t("validations.email_max")),

        mobile_number: yup
            .string()
            .trim()
            .required(t("validations.mobile_number_required"))
            .matches(notBlankRegex, t("validations.mobile_number_invalid_format"))
            .matches(/^\d+$/, t("validations.mobile_number_only_digits"))
            .min(8, t("validations.mobile_number_min"))
            .max(15, t("validations.mobile_number_max"))
            .test("not-all-zeros", t("validations.mobile_number_all_zeros"), (value) => !/^0+$/.test(value)),

        country_code: yup
            .string()
            .trim()
            .required(t("validations.country_code_required"))
            .max(5, t("validations.country_code_max")),

        alternate_number: yup
            .string()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .trim()
            .test("not-blank", t("validations.alternate_number_invalid_format"), (value) => !value || notBlankRegex.test(value))
            .test("is-valid", t("validations.alternate_number_invalid"), (value) => !value || /^[0-9]{8,15}$/.test(value))
            .test("not-all-zeros", t("validations.mobile_number_all_zeros"), (value) => !value || !/^0+$/.test(value)),

        alternate_country_code: yup
            .string()
            .nullable()
            .trim()
            .max(5, t("validations.alternate_country_code_max"))
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .when("alternate_number", {
                is: (val) => val && val.trim() !== "",
                then: (schema) => schema.required(t("validations.alternate_country_code_required")),
                otherwise: (schema) => schema.nullable(),
            }),

        profile_image: fileValidation(t, "profile_image", isEdit, isImageRemoved),
        id_proof: fileValidation(t, "id_proof", isEdit, isImageRemoved),

        residence_id: yup
            .string()
            .trim()
            .required(t("validations.residence_id_required"))
            .matches(notBlankRegex, t("validations.residence_id_invalid_format"))
            .matches(noHTMLRegex, t("validations.no_html_tags"))
            .matches(noCodeRegex, t("validations.no_code_content"))
            .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("residence_id_or_iqama_id") }))
            .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("residence_id_or_iqama_id") }))
            .min(2, t("validations.residence_id_min_max"))
            .max(30, t("validations.residence_id_min_max")),

        role_id: yup.string().trim().required(t("validations.role_required")),
        country_id: yup.string().trim().required(t("validations.country_required")),
        city_id: yup.string().trim().required(t("validations.city_required")),
        office_id: yup.string().trim().required(t("validations.office_required")),

        parent_id: yup
            .string()
            .nullable()
            .trim()
            .when("designation", {
                is: "member",
                then: (schema) => schema.required(t("validations.manager_required")),
                otherwise: (schema) => schema.nullable(),
            }),
    });

const isImageRemoved = {
    id_proof: false,
    profile_image: false,
};

export function AddInternalUser({ type = 'city_representative', isEdit = false, isView = false }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation("internal-user");

    const typeLabelMap = {
        'City Representative': 'logistics',
        'Sales Representative': 'sales',
        'Vendor': 'vendor',
        'Customer': 'customer',
        'Sub Admin': 'sub_admin',
        'Finance': 'finance',
        'Procurement': 'procurement',
        'Delivery Partner': 'delivery_partner',
        'Administrator': 'administrator',
        'city_representative': 'logistics',
        'sales_representative': 'sales',
        'delivery_partner': 'delivery_partner',
        'sub_admin': 'sub_admin'
    };

    const typeKey = typeLabelMap[type] || type?.toLowerCase()?.replace(/\s+/g, '_') || 'default';
    const displayType = t(`type_${typeKey}`, t(typeKey, t(type, type)));

    const dispatch = useDispatch();
    const page_url = type.replace(' ', '-')?.toLocaleLowerCase();
    const [isShowLoader, setIsShowLoader] = useState(true);

    const initialForm = {
        id: null,
        type: type,
        status: 'active',
        first_name: '',
        gender: '',
        email: '',
        mobile_number: null,
        country_code: null,
        alternate_number: null,
        alternate_country_code: null,
        designation: 'member',
        profile_image: '',
        residence_id: null,
        id_proof: '',
        role_id: null,
        country_id: null,
        city_id: null,
        office_id: null,
        sales_work_type: '',
        permissions: [],
        parent_id: ''
    };

    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset, setError } = useForm({
        resolver: yupResolver(schema(t, isEdit, isImageRemoved)),
        defaultValues: initialForm
    });

    const { mobile_country_code } = useSelector((state) => state.internalUser);
    const selectedInternalUser = useSelector((s) => s.internalUser.selected);

    useEffect(() => {
        dispatch(fetchRole());
        dispatch(fetchCountry());
        dispatch(getManager(type));
        dispatch(fetchCountryMobileCode());
        if (!isEdit) {
            setIsShowLoader(false);
        }
    }, [dispatch]);

    const { roles, role_permissions, cities, countries, offices, managers, loading } = useSelector((state) => state.internalUser);

    useEffect(() => {
        let role = roles?.find(opt => opt.label === type);
        if (role) {
            setValue('role_id', role?.value, { shouldValidate: true });
            dispatch(fetchRolePermission(role?.value));
        }
    }, [roles, dispatch]);

    useEffect(() => {
        if (type === 'Sub Admin') {
            setValue('designation', 'manager', { shouldValidate: true });
            setValue('parent_id', null, { shouldValidate: true });
        }
    }, [type, roles]);

    useEffect(() => {
        if (isEdit) {
            if (selectedInternalUser && String(selectedInternalUser.id) === String(id)) {
                dispatch(fetchCity(selectedInternalUser.country_id)).then(() => {
                    dispatch(fetchOffice(selectedInternalUser.city_id)).then(() => {
                        reset({ ...selectedInternalUser });
                        setIsShowLoader(false);
                    });
                });
            }
        } else {
            reset(initialForm);
        }
    }, [isEdit, id, selectedInternalUser, dispatch]);

    const idProof = watch("id_proof");
    const profileImage = watch('profile_image');

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(addInternalUser(data)).unwrap();
            if (result?.status) {
                let action = data?.id ? 'updated' : 'added';
                Toaster.success(
                    t(
                        data?.id ? 'user_updated_successfully' : 'user_added_successfully',
                        { type: displayType }
                    )
                );
                dispatch(resetStatus());
                reset();
                navigate(`/dashboard/${page_url}`);
            } else {
                Toaster.error(result?.message || t("something_went_wrong"));
                dispatch(resetStatus());
                if (result?.data) {
                    setError(result?.data?.field, {
                        type: "manual",
                        message: result?.data?.message,
                    });
                }
            }
        } catch (err) {
            Toaster.error(err?.message || t("failed_to_add_user"));
            dispatch(resetStatus());
        }
    };

    const removeImage = (field) => {
        setValue(field, null, { shouldValidate: false });
        if (isEdit) {
            isImageRemoved[field] = true;
        }
    }

    const handlePermissionChange = (permission_name, is_check) => {
        if (is_check === "no") {
            let permissions = watch("permissions") || [];
            if (permissions.includes(permission_name)) {
                permissions = permissions.filter((p) => p !== permission_name);
            } else {
                permissions = [...permissions, permission_name];
            }
            setValue("permissions", permissions);
        }
    };

    const handleCheckAllPermission = (e, perms) => {
        let currentPermissions = watch("permissions") || [];
        if (e.target.checked) {
            const newPermissions = perms
                .filter((perm) => perm.is_check === "no" && !currentPermissions.includes(perm.name))
                .map((perm) => perm.name);

            setValue("permissions", [...currentPermissions, ...newPermissions]);
        } else {
            const remainingPermissions = currentPermissions.filter(
                (permName) => !perms.some((perm) => perm.name === permName && perm.is_check === "no")
            );
            setValue("permissions", remainingPermissions);
        }
    };

    const isAllPermissionChecked = (permissions, watchedPermissions = []) => {
        return permissions.every(
            (perm) =>
                perm.is_check === "yes" || watchedPermissions.includes(perm.name)
        );
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader) && <ShowLoader />}
            <ScrollToTop />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
                        <div className="flex items-center gap-2 cursor-pointer">
                            <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                                {isView
                                    ? t('view_type', { type: displayType })
                                    : isEdit
                                        ? t('edit_type', { type: displayType })
                                        : t('add_new_type', { type: displayType })
                                }
                            </Typography>
                        </div>
                        {!isView &&
                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => navigate(`/dashboard/${page_url}`)}
                                    className="rounded-lg"
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                                    {loading ? t('saving') : isEdit ? t('update') : t('save')}
                                </Button>
                            </div>
                        }
                    </div>
                    <div className="flex items-center gap-1">
                        <Link to={`/dashboard/${page_url}`} className="flex items-center text-blue-600 hover:text-blue-800">
                            <ArrowLeftIcon className="h-4 w-4 mr-1" />
                            {t('back')}
                        </Link>
                    </div>
                </div>
                <fieldset disabled={isView}>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('basic_user_details')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('full_name')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_full_name')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("first_name")}
                                    error={Boolean(errors.first_name)}
                                />
                                {errors.first_name && (
                                    <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('gender')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_gender')}
                                    options={[
                                        { value: 'Male', label: t('male') },
                                        { value: 'Female', label: t('female') },
                                        { value: 'Other', label: t('other') }
                                    ]}
                                    onChange={(option) => {
                                        setValue('gender', option?.value, { shouldValidate: true });
                                    }}
                                    value={[
                                        { value: 'Male', label: t('male') },
                                        { value: 'Female', label: t('female') },
                                        { value: 'Other', label: t('other') }
                                    ].find(opt => opt.value === watch('gender')) || null}
                                    isClearable
                                    isDisabled={isView}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: '44px',
                                        }),
                                    }}
                                />
                                {errors.gender && (
                                    <p className="text-red-500 text-sm">{errors.gender?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('email_address')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="email"
                                    size="lg"
                                    placeholder={t('enter_email_address')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("email")}
                                    error={Boolean(errors.email)}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm">{errors.email?.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('phone_number')} <span className="text-red-500">*</span>{t('with_whatsapp_access')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            size="lg"
                                            className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            placeholder={t('select')}
                                            onChange={(code) => {
                                                setValue('country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('enter_phone_number')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("mobile_number")}
                                            error={Boolean(errors.mobile_number)}
                                        />
                                    </div>
                                </div>
                                {(errors.mobile_number || errors.country_code) && (
                                    <p className="text-red-500 text-sm">{errors.mobile_number?.message ? errors.mobile_number?.message : errors.country_code.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('alternate_number')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            placeholder={t('select')}
                                            onChange={(code) => {
                                                setValue('alternate_country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('alternate_country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.alternate_country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('enter_alternate_number')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("alternate_number")}
                                            error={Boolean(errors.alternate_number)}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (!value || value.trim() === "") {
                                                    setValue("alternate_number", '', { shouldValidate: true });
                                                    setValue("alternate_country_code", null, { shouldValidate: true });
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                {(errors.alternate_number || errors.alternate_country_code) && (
                                    <p className="text-red-500 text-sm">{errors.alternate_number?.message ? errors.alternate_number?.message : errors.alternate_country_code.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('profile_photo')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    {!profileImage ?
                                        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 ml-3 mr-5">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={1.5}
                                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM4 20c0-2.67 5.33-4 8-4s8 1.33 8 4"
                                                />
                                            </svg>
                                        </div>

                                        :
                                        <div className="relative w-16 h-16 ml-3 mr-5">
                                            <FilePreview
                                                file={profileImage}
                                                onRemove={() => removeImage('profile_image')}
                                                isView={isView}
                                                containerClass="relative w-16 h-16 rounded-full border-2 border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50"
                                            />
                                        </div>
                                    }
                                    <input
                                        id="profilePhoto"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files[0] || null;
                                            setValue('profile_image', file, { shouldValidate: true });
                                        }}
                                    /><p className="text-xs text-gray-400 mt-1">{t('jpg_png_upto_2mb')}</p>
                                    <label
                                        htmlFor="profilePhoto"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                {errors.profile_image && (
                                    <p className="text-red-500 text-sm mt-2">{errors.profile_image?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('residence_id_or_iqama_id')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="text"
                                        size="lg"
                                        placeholder={t('enter_residence_id_or_iqama_id')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("residence_id")}
                                        error={Boolean(errors.residence_id)}
                                    />

                                    <input
                                        id="residenceNumberFile"
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files[0] || null;
                                            setValue('id_proof', file, { shouldValidate: true });
                                        }}
                                    />
                                    <label
                                        htmlFor="residenceNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                <p className="text-xs text-gray-400">{t('jpg_png_upto_2mb')} </p>
                                {(errors.residence_id?.message || errors.id_proof?.message) && (
                                    <p className="text-red-500 text-sm mt-2">
                                        {errors.residence_id?.message || errors.id_proof?.message}
                                    </p>
                                )}
                                {idProof && <>
                                    <div className="mt-3">
                                        <FilePreview
                                            file={idProof}
                                            onRemove={() => removeImage('id_proof')}
                                            isView={isView}
                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                        />
                                    </div>
                                </>}
                            </div>
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assign_office')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="md:col-span-2 w-full flex items-center gap-3">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('is_this_person_an_office_incharge')}
                                </Typography>

                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        value="office"
                                        {...register("sales_work_type")}
                                        checked={watch('sales_work_type') == 'office'}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 text-sm">{t('yes')}</span>
                                </label>
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('country')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_country')}
                                    onChange={(country) => {
                                        setValue('country_id', country?.value, { shouldValidate: true });
                                        dispatch(fetchCity(country?.value));
                                    }}
                                    error={Boolean(errors.country_id)}
                                    options={countries}
                                    value={countries?.find(opt => opt.value === watch('country_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{errors.country_id?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('city_territory')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_city')}
                                    onChange={(city) => {
                                        setValue('city_id', city?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(city?.value))
                                    }}
                                    error={Boolean(errors.city_id)}
                                    options={cities}
                                    value={cities?.find(opt => opt.value === watch('city_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{errors.city_id?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('office')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_office')}
                                    onChange={(office) => {
                                        setValue('office_id', office?.value, { shouldValidate: true });
                                    }}
                                    error={Boolean(errors.office_id)}
                                    options={offices}
                                    value={offices?.find(opt => opt.value === watch('office_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{errors.office_id?.message}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assign_role')}
                            </Typography>
                        </div>
                        <div className="px-0 pt-0 pb-2 p-6">
                            <div className="mb-4 w-full flex items-center gap-3">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    {t('is_this_person_on_a_manager_role')}
                                </Typography>

                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        // value="manager"
                                        // {...register("designation")}
                                        checked={watch('designation') === 'manager'}
                                        disabled={watch('designation') === 'manager' && type === 'Sub Admin'}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setValue('designation', 'manager', { shouldValidate: true });
                                                setValue('parent_id', '', { shouldValidate: true });
                                            } else {
                                                setValue('designation', 'member', { shouldValidate: true });
                                                setValue('parent_id', '', { shouldValidate: false });
                                            }
                                        }}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 text-sm">{t('yes')}</span>
                                </label>
                            </div>
                            <div className={`mb-4 w-full grid grid-cols-1 md:grid-cols-${type !== 'Sub Admin' ? '2' : 1} gap-6`}>
                                <div className="flex flex-col gap-2">
                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                        {t('select_role')} <span className="text-red-500">*</span>
                                    </Typography>
                                    <Select
                                        placeholder={t('select_role')}
                                        error={Boolean(errors.role_id)}
                                        options={roles}
                                        value={roles?.find(opt => opt.label === type)}
                                        isDisabled={true}
                                    />
                                    {errors.role_id && (
                                        <p className="text-red-500 text-sm">{errors.role_id?.message}</p>
                                    )}
                                </div>
                                {type !== 'Sub Admin' &&
                                    <div className="flex flex-col gap-2">
                                        <Typography variant="small" color="blue-gray" className="font-medium">
                                            {t('select_manager')} <span className="text-red-500">*</span>
                                        </Typography>
                                        <Select
                                            placeholder={t('select_manager')}
                                            error={Boolean(errors.parent_id)}
                                            options={managers}
                                            value={managers?.find(opt => opt.value === selectedInternalUser?.parent_id)}
                                            isDisabled={watch("designation") === "manager" || isView}
                                            onChange={(manager) => {
                                                setValue('parent_id', manager.value);
                                            }}
                                        />
                                        {errors.parent_id && (
                                            <p className="text-red-500 text-sm">{errors.parent_id?.message}</p>
                                        )}
                                    </div>
                                }
                            </div>

                        </div>
                    </div>

                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assign_permissions')}
                            </Typography>
                            <Typography variant="small" color="gray" className="-mb-3 font-medium">
                                {t('pre_configured_for_sales_rep')}
                            </Typography>
                        </div>
                        <div className="px-0 pt-0 pb-2 p-6">
                            <table className="w-full min-w-max table-auto text-left border border-gray-200">
                                <thead>
                                    <tr>
                                        <th className="p-4 border-b border-gray-200">{t('module')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('all')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('view')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('add')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('edit')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('delete')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {role_permissions?.permissions && Object.entries(role_permissions?.permissions).map(([module, perms]) => {
                                        return (
                                            <tr key={module} className="hover:bg-gray-50">
                                                <td className="p-4 font-medium">{t(module)}</td>
                                                <td className="p-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        value="yes"
                                                        onChange={(e) => {
                                                            handleCheckAllPermission(e, perms);
                                                        }}
                                                        checked={isAllPermissionChecked(perms, watch("permissions"))}
                                                        disabled={isAllPermissionChecked(perms, register.permissions)}
                                                        className="w-4 h-4 cursor-pointer accent-green"
                                                    />
                                                    <p className="capitalize text-gray-500 text-sm">
                                                        {t('all')}
                                                    </p>
                                                </td>
                                                {perms.map((perm) => (
                                                    <td key={perm.name} className="p-4 text-center">
                                                        <input
                                                            type="checkbox"
                                                            value="yes"
                                                            checked={perm.is_check === "yes" || watch('permissions').includes(perm.name)}
                                                            disabled={perm.is_check === "yes"}
                                                            className={`w-4 h-4 cursor-pointer ${perm.is_check === "yes" ? "accent-gray-400 cursor-not-allowed" : "accent-green"
                                                                }`}
                                                            onChange={() => {
                                                                if (perm.is_check === "no") {
                                                                    handlePermissionChange(perm.name, perm.is_check);
                                                                }
                                                            }}
                                                        />
                                                        <p className="capitalize text-gray-500 text-sm">
                                                            {t(perm.name.split('.').pop() !== 'update' ? perm.name.split('.').pop() : 'edit')}
                                                        </p>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </fieldset>
            </form>
        </div>
    );
}

export default AddInternalUser;